import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MessageCircle, X, Send, ArrowLeft, Loader2, Users, Briefcase, Mic, Square, Play, Pause, Volume2, MoreVertical, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';

const TeamChatWidget = () => {
  const { api, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('conversations'); // 'conversations' | 'chat' | 'users' | 'agents' | 'agent-chat'
  const [conversations, setConversations] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [agentUnread, setAgentUnread] = useState(0);
  const [agentChats, setAgentChats] = useState([]);
  const [selectedAgentChat, setSelectedAgentChat] = useState(null);
  const [agentMessages, setAgentMessages] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Delete message state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      fetchUsers();
      fetchAgentChats();
    }
  }, [isOpen]);

  useEffect(() => {
    // Poll for unread count
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchAgentUnreadCount();
    }, 15000);
    fetchUnreadCount();
    fetchAgentUnreadCount();
    return () => clearInterval(interval);
  }, []);

  // Listen for openTeamChat event from application detail page
  useEffect(() => {
    const handleOpenTeamChat = (event) => {
      setIsOpen(true);
      setView('users'); // Go directly to team members list to start a new conversation
      // Optionally, we could pre-select the application context
      if (event.detail?.applicationId) {
        // Store context for reference in the chat
        setNewMessage(`Re: Application #${event.detail.applicationName || event.detail.applicationId}\n\n`);
      }
    };
    
    window.addEventListener('openTeamChat', handleOpenTeamChat);
    return () => window.removeEventListener('openTeamChat', handleOpenTeamChat);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (view === 'chat' && selectedUser) {
      const interval = setInterval(() => fetchMessages(true), 5000);
      return () => clearInterval(interval);
    }
  }, [view, selectedUser]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api().get('/chat/unread-count');
      setTotalUnread(response.data.general_unread || 0);
    } catch (error) {}
  };

  const fetchAgentUnreadCount = async () => {
    try {
      const response = await api().get('/agent-chats/unread-count');
      setAgentUnread(response.data.unread_count || 0);
    } catch (error) {}
  };

  const fetchAgentChats = async () => {
    try {
      const response = await api().get('/agent-chats/list');
      setAgentChats(response.data.chats || []);
    } catch (error) {
      console.error('Failed to load agent chats');
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await api().get('/team-chat/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api().get('/users');
      setAllUsers((response.data || []).filter(u => u.id !== user?.id));
    } catch (error) {}
  };

  const fetchMessages = async (silent = false) => {
    if (!selectedUser) return;
    if (!silent) setLoading(true);
    try {
      const response = await api().get(`/team-chat/${selectedUser.id}`);
      setMessages(response.data.messages || []);
      // Mark as read
      await api().post(`/team-chat/${selectedUser.id}/mark-read`);
      fetchUnreadCount();
    } catch (error) {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const openChat = (chatUser) => {
    setSelectedUser(chatUser);
    setView('chat');
    fetchMessagesForUser(chatUser.id || chatUser.user_id);
  };

  const fetchMessagesForUser = async (userId) => {
    setLoading(true);
    try {
      const response = await api().get(`/team-chat/${userId}`);
      setMessages(response.data.messages || []);
      setSelectedUser(response.data.other_user || { id: userId, name: 'Unknown' });
      await api().post(`/team-chat/${userId}/mark-read`);
      fetchUnreadCount();
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    setSending(true);
    try {
      await api().post('/team-chat', {
        recipient_id: selectedUser.id || selectedUser.user_id,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(true);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Agent chat functions
  const openAgentChat = async (chat) => {
    setSelectedAgentChat(chat);
    setView('agent-chat');
    setLoading(true);
    try {
      const response = await api().get(`/applications/${chat.application_id}/agent-chat`);
      setAgentMessages(response.data.messages || []);
      // Mark as read
      await api().post(`/applications/${chat.application_id}/agent-chat/mark-read`);
      fetchAgentUnreadCount();
    } catch (error) {
      toast.error('Failed to load agent messages');
    } finally {
      setLoading(false);
    }
  };

  const sendAgentReply = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedAgentChat) return;
    
    setSending(true);
    try {
      await api().post(`/applications/${selectedAgentChat.application_id}/agent-chat`, {
        message: newMessage
      });
      setNewMessage('');
      // Refresh messages
      const response = await api().get(`/applications/${selectedAgentChat.application_id}/agent-chat`);
      setAgentMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please allow permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setAudioBlob(null);
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const sendVoiceNote = async () => {
    if (!audioBlob || !selectedUser) return;
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice_note.webm');
      formData.append('recipient_id', selectedUser.id || selectedUser.user_id);
      formData.append('duration', recordingDuration.toString());
      
      await api().post('/team-chat/voice-note', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAudioBlob(null);
      setRecordingDuration(0);
      fetchMessages(true);
      toast.success('Voice message sent');
    } catch (error) {
      console.error('Voice note error:', error);
      toast.error('Failed to send voice message');
    } finally {
      setSending(false);
    }
  };

  // Voice note playback state
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioRefs = useRef({});

  const playVoiceNote = async (voiceNote) => {
    try {
      if (playingVoiceId === voiceNote.id) {
        // Stop current playback
        if (audioRefs.current[voiceNote.id]) {
          audioRefs.current[voiceNote.id].pause();
          audioRefs.current[voiceNote.id].currentTime = 0;
        }
        setPlayingVoiceId(null);
        return;
      }
      
      // Stop any other playing audio
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
      
      if (!audioRefs.current[voiceNote.id]) {
        const response = await api().get(`/team-chat/voice-note/${voiceNote.id}`, {
          responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([response.data]));
        const audio = new Audio(url);
        audio.onended = () => setPlayingVoiceId(null);
        audioRefs.current[voiceNote.id] = audio;
      }
      
      audioRefs.current[voiceNote.id].play();
      setPlayingVoiceId(voiceNote.id);
    } catch (error) {
      console.error('Play error:', error);
      toast.error('Failed to play voice note');
    }
  };

  // Delete message functions
  const confirmDeleteMessage = (msg) => {
    setMessageToDelete(msg);
    setDeleteDialogOpen(true);
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      await api().delete(`/team-chat/${messageToDelete.id}`);
      setMessages(messages.filter(m => m.id !== messageToDelete.id));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete message');
    } finally {
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  const renderConversationsList = () => {
    const items = [];
    for (let i = 0; i < conversations.length; i++) {
      const conv = conversations[i];
      items.push(
        <div
          key={conv.user_id}
          onClick={() => openChat({ id: conv.user_id, name: conv.user_name })}
          className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer border-b"
        >
          <Avatar className="w-10 h-10">
            <AvatarFallback>{getInitials(conv.user_name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{conv.user_name}</span>
              <span className="text-xs text-muted-foreground">{formatTime(conv.last_message_time)}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate">{conv.last_message}</p>
          </div>
          {conv.unread_count > 0 && (
            <Badge className="bg-destructive text-destructive-foreground">{conv.unread_count}</Badge>
          )}
        </div>
      );
    }
    return items;
  };

  const renderUsersList = () => {
    const items = [];
    for (let i = 0; i < allUsers.length; i++) {
      const u = allUsers[i];
      items.push(
        <div
          key={u.id}
          onClick={() => openChat(u)}
          className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer border-b"
        >
          <Avatar className="w-10 h-10">
            <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="font-medium text-sm">{u.name}</p>
            <p className="text-xs text-muted-foreground">{u.email}</p>
          </div>
        </div>
      );
    }
    return items;
  };

  const renderMessagesList = () => {
    const items = [];
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const isOwn = msg.sender_id === user?.id;
      const hasVoiceNote = msg.is_voice_note && msg.voice_note;
      
      items.push(
        <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}>
          <div className={`max-w-[80%] ${isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-3 py-2 relative`}>
            {/* Delete menu for own messages */}
            {isOwn && (
              <div className="absolute -left-8 top-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded hover:bg-muted/50" data-testid={`team-msg-menu-${msg.id}`}>
                      <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-32">
                    <DropdownMenuItem 
                      onClick={() => confirmDeleteMessage(msg)}
                      className="text-destructive focus:text-destructive cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
            
            {msg.application_id && (
              <div className="text-xs opacity-75 mb-1 border-b border-current/20 pb-1">
                Re: {msg.application_name || msg.application_id}
              </div>
            )}
            
            {hasVoiceNote ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => playVoiceNote(msg.voice_note)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                    isOwn 
                      ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30' 
                      : 'bg-primary/10 hover:bg-primary/20'
                  }`}
                >
                  {playingVoiceId === msg.voice_note.id ? (
                    <Pause className={`w-4 h-4 ${isOwn ? 'text-primary-foreground' : 'text-primary'}`} />
                  ) : (
                    <Play className={`w-4 h-4 ml-0.5 ${isOwn ? 'text-primary-foreground' : 'text-primary'}`} />
                  )}
                </button>
                <div className="flex-1">
                  <div className={`h-1 rounded-full ${isOwn ? 'bg-primary-foreground/30' : 'bg-primary/20'}`}>
                    <div className={`h-full rounded-full ${isOwn ? 'bg-primary-foreground/60' : 'bg-primary/50'}`} style={{ width: playingVoiceId === msg.voice_note.id ? '60%' : '0%' }} />
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Volume2 className={`w-3 h-3 ${isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground'}`} />
                    <span className={`text-xs ${isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                      {formatDuration(msg.voice_note.duration || 0)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            )}
            
            <p className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
              {formatTime(msg.created_at)}
            </p>
          </div>
        </div>
      );
    }
    return items;
  };

  const renderAgentChatsList = () => {
    const items = [];
    for (let i = 0; i < agentChats.length; i++) {
      const chat = agentChats[i];
      items.push(
        <div
          key={chat.application_id}
          onClick={() => openAgentChat(chat)}
          className="flex items-center gap-3 p-3 hover:bg-orange-50 cursor-pointer border-b"
        >
          <Avatar className="w-10 h-10 bg-orange-100">
            <AvatarFallback className="bg-orange-100 text-orange-600">
              {getInitials(chat.agent_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{chat.agent_name}</span>
              <span className="text-xs text-muted-foreground">{formatTime(chat.last_message_time)}</span>
            </div>
            <p className="text-xs text-muted-foreground truncate">{chat.student_name}</p>
            <p className="text-xs text-muted-foreground truncate">{chat.last_message}</p>
          </div>
          {chat.unread_count > 0 && (
            <Badge className="bg-orange-500 text-white">{chat.unread_count}</Badge>
          )}
        </div>
      );
    }
    return items;
  };

  const renderAgentMessagesList = () => {
    const items = [];
    for (let i = 0; i < agentMessages.length; i++) {
      const msg = agentMessages[i];
      const isFromAgent = msg.sender_type === 'agent';
      items.push(
        <div key={msg.id} className={`flex ${isFromAgent ? 'justify-start' : 'justify-end'} mb-2`}>
          <div className={`max-w-[80%] ${isFromAgent ? 'bg-orange-100 text-orange-900' : 'bg-primary text-primary-foreground'} rounded-lg px-3 py-2`}>
            <p className="text-xs font-medium mb-1">{isFromAgent ? msg.sender_name || 'Agent' : 'You'}</p>
            <p className="text-sm">{msg.message}</p>
            <p className={`text-xs mt-1 ${isFromAgent ? 'text-orange-600' : 'text-primary-foreground/70'}`}>
              {formatTime(msg.created_at)}
            </p>
          </div>
        </div>
      );
    }
    return items;
  };

  if (!isOpen) {
    const totalBadge = totalUnread + agentUnread;
    return (
      <div className="fixed bottom-20 right-6 z-50 flex items-center gap-2">
        <span
          className="hidden sm:inline-flex select-none items-center gap-1 px-2.5 py-1 rounded-full
                     bg-white/95 backdrop-blur border border-slate-200 text-[11px] font-semibold
                     text-slate-700 shadow-sm whitespace-nowrap"
          aria-hidden
        >
          Team Chat
        </span>
        <Button
          onClick={() => setIsOpen(true)}
          className="relative rounded-full w-14 h-14 shadow-lg"
          data-testid="team-chat-fab"
          title="Team Chat"
        >
          <MessageCircle className="w-6 h-6" />
          {totalBadge > 0 && (
            <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </Button>
      </div>
    );
  }

  return (
    <Card className="fixed bottom-20 right-6 w-80 h-[500px] shadow-xl z-50 flex flex-col">
      <CardHeader className="py-3 px-4 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          {view === 'chat' ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => { setView('conversations'); setSelectedUser(null); }}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <span className="font-medium text-sm">{selectedUser?.name}</span>
            </>
          ) : view === 'agent-chat' ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => { setView('agents'); setSelectedAgentChat(null); }}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <span className="font-medium text-sm text-orange-600">{selectedAgentChat?.agent_name}</span>
            </>
          ) : (
            <>
              <CardTitle className="text-base">Team Chat</CardTitle>
              <div className="flex gap-1">
                <Button
                  variant={view === 'conversations' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setView('conversations')}
                  title="Team Conversations"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
                <Button
                  variant={view === 'agents' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => { setView('agents'); fetchAgentChats(); }}
                  className="relative"
                  title="Agent Messages"
                >
                  <Briefcase className="w-4 h-4" />
                  {agentUnread > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {agentUnread > 9 ? '9+' : agentUnread}
                    </span>
                  )}
                </Button>
                <Button
                  variant={view === 'users' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setView('users')}
                  title="All Users"
                >
                  <Users className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden flex flex-col">
        {view === 'conversations' && (
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No conversations yet</p>
                <Button variant="link" size="sm" onClick={() => setView('users')}>Start a new chat</Button>
              </div>
            ) : (
              renderConversationsList()
            )}
          </ScrollArea>
        )}

        {view === 'users' && (
          <ScrollArea className="flex-1">
            {allUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No team members found</div>
            ) : (
              renderUsersList()
            )}
          </ScrollArea>
        )}

        {view === 'chat' && (
          <>
            <ScrollArea className="flex-1 p-3">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No messages yet</div>
              ) : (
                <>
                  {renderMessagesList()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </ScrollArea>
            
            {/* Voice Recording UI */}
            {isRecording && (
              <div className="px-3 py-2 bg-red-50 border-t flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-600 font-medium flex-1">Recording {formatDuration(recordingDuration)}</span>
                <Button size="sm" variant="ghost" onClick={cancelRecording} className="h-7 text-xs">
                  <X className="w-3 h-3 mr-1" />Cancel
                </Button>
                <Button size="sm" onClick={stopRecording} className="h-7 bg-red-500 hover:bg-red-600 text-xs">
                  <Square className="w-3 h-3 mr-1" />Stop
                </Button>
              </div>
            )}
            
            {/* Voice Note Preview */}
            {audioBlob && !isRecording && (
              <div className="px-3 py-2 bg-emerald-50 border-t flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-emerald-700 font-medium flex-1">Voice ready ({formatDuration(recordingDuration)})</span>
                <Button size="sm" variant="ghost" onClick={cancelRecording} className="h-7 text-xs text-destructive">
                  Discard
                </Button>
                <Button size="sm" onClick={sendVoiceNote} disabled={sending} className="h-7 bg-emerald-500 hover:bg-emerald-600 text-xs">
                  {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Send
                </Button>
              </div>
            )}
            
            {!isRecording && !audioBlob && (
              <form onSubmit={sendMessage} className="p-3 border-t flex gap-2 items-end">
                <Button 
                  type="button" 
                  size="icon" 
                  variant="ghost"
                  onClick={startRecording}
                  className="h-10 w-10 flex-shrink-0"
                  data-testid="team-voice-btn"
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                  placeholder="Type a message... (Ctrl+Enter to send)"
                  className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                  rows={1}
                  data-testid="team-chat-input"
                />
                <Button type="submit" size="icon" disabled={sending || !newMessage.trim()} data-testid="team-chat-send" className="h-10 w-10">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            )}
          </>
        )}

        {view === 'agents' && (
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : agentChats.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="w-12 h-12 mx-auto mb-2 text-orange-300" />
                <p className="text-sm">No agent messages yet</p>
                <p className="text-xs text-muted-foreground">Messages from agents will appear here</p>
              </div>
            ) : (
              renderAgentChatsList()
            )}
          </ScrollArea>
        )}

        {view === 'agent-chat' && (
          <>
            <div className="px-3 py-2 bg-orange-50 border-b">
              <p className="text-xs text-orange-600 font-medium">Re: {selectedAgentChat?.student_name}</p>
            </div>
            <ScrollArea className="flex-1 p-3">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : agentMessages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No messages yet</div>
              ) : (
                <>
                  {renderAgentMessagesList()}
                  <div ref={messagesEndRef} />
                </>
              )}
            </ScrollArea>
            <form onSubmit={sendAgentReply} className="p-3 border-t flex gap-2 items-end">
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    sendAgentReply(e);
                  }
                }}
                placeholder="Reply to agent... (Ctrl+Enter to send)"
                className="flex-1 min-h-[44px] max-h-[120px] resize-none"
                rows={1}
              />
              <Button type="submit" size="icon" disabled={sending || !newMessage.trim()} className="bg-orange-500 hover:bg-orange-600 h-10 w-10">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </>
        )}
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMessage} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default TeamChatWidget;
